import {IconNames} from '@wandb/weave/components/Icon';
import React, {useState} from 'react';

import {SemanticLoader as Loader} from '../../../components/utility/InstrumentedLoader';
import {useFetchProjectJobsQuery} from '../../../generated/graphql';
import {usePublicQueueAccess} from '../hooks';
import {JobDrawer} from '../JobDrawer/JobDrawer';
import {getPublicJobInfo, JobEdge} from '../Jobs/jobUtil';
import {PublicJobCard} from '../Jobs/PublicJobCard';
import {WANDB_ENTITY_NAME, WANDB_JOBS_PROJECT_NAME} from '../utils';
import {InfoBanner} from './InfoBanner';

export const TabPublicJobs: React.FC = () => {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();
  const publicJobsQuery = useFetchProjectJobsQuery({
    variables: {
      entityName: WANDB_ENTITY_NAME,
      projectName: WANDB_JOBS_PROJECT_NAME,
    },
    skip: !hasPublicQueueAccess,
  });

  if (isFeatureFlagLoading || publicJobsQuery.loading) {
    return <Loader active name="public-jobs-tab-loader" />;
  }

  const publicJobEdges: JobEdge[] =
    (publicJobsQuery.data?.project?.artifactType?.artifactCollections
      ?.edges as JobEdge[]) ?? [];

  const publicJobs = getPublicJobInfo(
    publicJobEdges,
    WANDB_ENTITY_NAME,
    WANDB_JOBS_PROJECT_NAME
  );

  const onJobLaunch = (jobId: string) => {
    setSelectedJobId(jobId);
  };

  const closeDrawer = () => {
    setSelectedJobId(null);
  };

  if (publicJobs.length === 0) {
    return (
      <div className="flex items-center justify-center px-6 py-12 text-base text-moon-600">
        No public jobs available
      </div>
    );
  }

  return (
    <>
      <InfoBanner
        hintKey="public-jobs-info-banner"
        icon={IconNames.Info}
        bannerContent="Run reproducible W&B public jobs directly in your project on a CoreWeave hardware queue."
      />
      <div className="grid grid-cols-1 gap-16 md:grid-cols-2 xl:grid-cols-3">
        {publicJobs.map(job => (
          <PublicJobCard
            key={job.id}
            job={job}
            onLaunch={() => onJobLaunch(job.id)}
          />
        ))}
      </div>

      <JobDrawer
        open={selectedJobId != null}
        closeDrawer={closeDrawer}
        entityName={WANDB_ENTITY_NAME}
        projectName={WANDB_JOBS_PROJECT_NAME}
        defaultJobId={selectedJobId ?? undefined}
        defaultJobVersion="latest"
      />
    </>
  );
};
